/**
 * A deneme sınavı (imported mock exam) as the learner reads it: one row per deneme on /reports, and the
 * whole deneme as a "karne" on /reports/mock-exams/:mockExamId.
 *
 * A deneme has no single ders — it spans every lesson it tested — so nothing here filters by coterie.
 * The backend sends the list as one item per (deneme, ders) and the karne as the learner's raw LESSON /
 * OUTCOME score rows; this turns both into what the screens print. Pure: callers translate.
 */

import { buildCategoryActivityAnswerDTOList } from "./ReportDetailBuilder";

export type MockExamFormat = "LGS" | "TYT" | "AYT" | "OTHER" | null | undefined;

/**
 * Where a deneme's results came from: imported from an outside provider (PDF / SPREADSHEET) or prepared
 * in-house by the school (IN_HOUSE, a kurum içi deneme). Every kind is the same deneme to the
 * learner — same wording, routes, colours and rank; only a kurum içi deneme can carry OPEN_ENDED rows.
 * Null on older data.
 */
export type MockExamSourceKind = "PDF" | "SPREADSHEET" | "IN_HOUSE";

/** OPEN_ENDED rows are one per open-ended question (percent only): never part of D/Y/B/net or Tahmini puan. */
export const OPEN_ENDED_SCORE_SCOPE = "OPEN_ENDED";

export interface MockExamScoreRow {
  scoreScope: "LESSON" | "OUTCOME" | "OPEN_ENDED" | string;
  rawLessonLabel?: string | null;
  rawLabel?: string | null;
  categoryId?: number | null;
  coterieType?: string | null;
  questionCount?: number | null;
  correctCount?: number | null;
  wrongCount?: number | null;
  netScore?: number | null;
  percentScore?: number | null;
}

export interface MockExamTotals {
  question: number;
  correct: number;
  wrong: number;
  blank: number;
  /** Null only when nothing could be counted. */
  net: number | null;
  /** 0–100, or null when there were no questions. */
  percent: number | null;
}

export interface MockExamOutcomeLine extends MockExamTotals {
  key: string;
  categoryId: number | null;
  rawLabel: string;
}

export interface MockExamLesson {
  key: string;
  /** The lesson label printed in the file (e.g. "Biyoloji.09"); "" when the file had none. */
  rawLessonLabel: string;
  coterieType: string | null;
  totals: MockExamTotals;
  outcomes: MockExamOutcomeLine[];
}

const num = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * The net of a row: the file's own net when it printed one, otherwise doğru − yanlış / k — k = 3 for LGS
 * (MEB: üç yanlış bir doğruyu götürür) and 4 for TYT, AYT and every other deneme (ÖSYM's rule). The same
 * rule the staff console applies, so a learner and their teacher read the same net.
 */
export const computeNet = (
  correct: number | null | undefined,
  wrong: number | null | undefined,
  format: MockExamFormat | string,
  sourceNet?: number | null,
): number | null => {
  if (sourceNet !== null && sourceNet !== undefined && Number.isFinite(Number(sourceNet))) {
    return Number(sourceNet);
  }
  if (correct == null && wrong == null) return null;
  const divisor = format === "LGS" ? 3 : 4;
  return num(correct) - num(wrong) / divisor;
};

/** Boş = soru − doğru − yanlış, never below 0 (a cancelled question can make the file's counts overlap). */
export const blankOf = (question: unknown, correct: unknown, wrong: unknown): number =>
  Math.max(0, num(question) - num(correct) - num(wrong));

const percentOf = (correct: number, question: number): number | null =>
  question > 0 ? (correct / question) * 100 : null;

const totalsOfRow = (row: MockExamScoreRow, format: MockExamFormat | string): MockExamTotals => {
  const question = num(row.questionCount);
  const correct = num(row.correctCount);
  const wrong = num(row.wrongCount);
  return {
    question,
    correct,
    wrong,
    blank: blankOf(question, correct, wrong),
    net: computeNet(row.correctCount, row.wrongCount, format, row.netScore),
    percent: percentOf(correct, question),
  };
};

/** Adds totals up; the net stays null only when none of the parts had one. */
export const sumTotals = (parts: MockExamTotals[]): MockExamTotals => {
  const question = parts.reduce((acc, part) => acc + part.question, 0);
  const correct = parts.reduce((acc, part) => acc + part.correct, 0);
  const wrong = parts.reduce((acc, part) => acc + part.wrong, 0);
  const blank = parts.reduce((acc, part) => acc + part.blank, 0);
  const netList = parts.map((part) => part.net).filter((net): net is number => net != null);
  return {
    question,
    correct,
    wrong,
    blank,
    net: netList.length ? netList.reduce((acc, net) => acc + net, 0) : null,
    percent: percentOf(correct, question),
  };
};

const lessonKeyOf = (row: MockExamScoreRow): string =>
  row.rawLessonLabel && row.rawLessonLabel.trim() ? row.rawLessonLabel.trim() : row.coterieType ?? "";

/**
 * The karne's lessons, in the order the rows arrive (the backend sorts them by lesson label). A lesson's totals are its LESSON row when the
 * file printed one (it also counts the topics nobody mapped); otherwise the sum of its OUTCOME rows.
 * Every OUTCOME row is kept — one without a categoryId is still a real result under its printed label.
 */
export const groupScoresByLesson = (
  scoreList: MockExamScoreRow[] | null | undefined,
  format: MockExamFormat | string,
): MockExamLesson[] => {
  const lessonByKey = new Map<string, { label: string; lessonRow: MockExamScoreRow | null; coterieType: string | null; outcomes: MockExamOutcomeLine[] }>();
  const ensure = (row: MockExamScoreRow) => {
    const key = lessonKeyOf(row);
    if (!lessonByKey.has(key)) {
      lessonByKey.set(key, { label: row.rawLessonLabel?.trim() ?? "", lessonRow: null, coterieType: row.coterieType ?? null, outcomes: [] });
    }
    const entry = lessonByKey.get(key)!;
    if (!entry.coterieType && row.coterieType) entry.coterieType = row.coterieType;
    if (!entry.label && row.rawLessonLabel?.trim()) entry.label = row.rawLessonLabel.trim();
    return { key, entry };
  };
  (scoreList ?? []).forEach((row, index) => {
    // Only LESSON / OUTCOME rows are counted rows; an OPEN_ENDED row (a kurum içi deneme's açık uçlu
    // question, percent only) must not even open a lesson, or "Açık uçlu" would show up as a 0-question ders.
    if (!row || (row.scoreScope !== "LESSON" && row.scoreScope !== "OUTCOME")) return;
    const { entry } = ensure(row);
    if (row.scoreScope === "LESSON") {
      if (!entry.lessonRow) entry.lessonRow = row;
      return;
    }
    entry.outcomes.push({
      key: `${row.categoryId ?? "raw"}-${row.rawLabel ?? ""}-${index}`,
      categoryId: row.categoryId ?? null,
      rawLabel: row.rawLabel ?? "",
      ...totalsOfRow(row, format),
    });
  });
  return Array.from(lessonByKey.entries()).map(([key, entry]) => ({
    key,
    rawLessonLabel: entry.label,
    coterieType: entry.coterieType,
    totals: entry.lessonRow ? totalsOfRow(entry.lessonRow, format) : sumTotals(entry.outcomes),
    outcomes: entry.outcomes,
  }));
};

/** The whole deneme: the sum of its lessons. */
export const overallTotals = (lessons: MockExamLesson[]): MockExamTotals =>
  sumTotals(lessons.map((lesson) => lesson.totals));

/** A /reports list row: the shape the backend sends per (deneme, ders) item. */
export interface MockExamPerformanceItem {
  examId?: number | null;
  name?: string;
  description?: string;
  coterieType?: string | null;
  createdAt?: number | null;
  examDate?: number | null;
  completionStatus?: string | null;
  learnerScore?: number | null;
  branchScore?: number | null;
  gradeScore?: number | null;
  activityScoreDetailDTOList?: unknown[] | null;
  estimatedScoreDTOList?: unknown[] | null;
  format?: string | null;
  /** Per exam, so identical on every (deneme, ders) item of that exam. */
  sourceKind?: MockExamSourceKind | null;
  [key: string]: unknown;
}

export interface MockExamListRow extends MockExamPerformanceItem {
  examId: number | null;
  coterieTypeList: string[];
}

const weightOf = (item: MockExamPerformanceItem): number =>
  Array.isArray(item.activityScoreDetailDTOList) && item.activityScoreDetailDTOList.length > 0
    ? item.activityScoreDetailDTOList.length
    : 1;

/** Question-weighted mean of a score field over the items that carry it; null when none does. */
const weightedScore = (
  items: MockExamPerformanceItem[],
  field: "learnerScore" | "branchScore" | "gradeScore",
): number | null => {
  let weightSum = 0;
  let scoreSum = 0;
  items.forEach((item) => {
    const value = item[field];
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return;
    const weight = weightOf(item);
    weightSum += weight;
    scoreSum += Number(value) * weight;
  });
  return weightSum > 0 ? scoreSum / weightSum : null;
};

/**
 * One row per deneme. The backend's items are per (deneme, ders) and each ders's learnerScore is the mean
 * of its question fractions, so the deneme's Başarı is the question-weighted mean over its items — the
 * same number a mean over every question would give. Tahmini puan is exam-level and identical on every
 * item, so the first item that has it supplies it. Items without an examId are kept as their own rows.
 */
export const groupMockExamItemsByExam = (
  items: MockExamPerformanceItem[] | null | undefined,
): MockExamListRow[] => {
  const groups = new Map<string, MockExamPerformanceItem[]>();
  (items ?? []).forEach((item, index) => {
    if (!item) return;
    const key = item.examId != null ? `exam-${item.examId}` : `item-${index}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  });
  return Array.from(groups.values()).map((group) => {
    const first = group[0];
    const withEstimate = group.find(
      (item) => Array.isArray(item.estimatedScoreDTOList) && item.estimatedScoreDTOList.length > 0,
    );
    const coterieTypeList: string[] = [];
    group.forEach((item) => {
      if (item.coterieType && !coterieTypeList.includes(item.coterieType)) coterieTypeList.push(item.coterieType);
    });
    return {
      ...first,
      examId: first.examId ?? null,
      learnerScore: weightedScore(group, "learnerScore") ?? 0,
      branchScore: weightedScore(group, "branchScore"),
      gradeScore: weightedScore(group, "gradeScore"),
      estimatedScoreDTOList: withEstimate?.estimatedScoreDTOList ?? first.estimatedScoreDTOList ?? [],
      coterieTypeList,
    };
  });
};

/** A node of the outcome tree OutcomeDetail renders (the Genel durum "kırılım"); only the fields it reads. */
export interface MockExamOutcomeTreeNode {
  categoryDTO: { id?: unknown; name: string; code: string; versionCode?: string; parentCode?: string };
  subCategoryActivityAnswerDTOList: MockExamOutcomeTreeNode[];
  aggregatedActivityCount?: number;
  aggregatedAveragePercentage?: number | null;
  [key: string]: unknown;
}

/**
 * The karne's "Öğrenme çıktısı kırılımı": this deneme's OUTCOME rows as the SAME curriculum tree Reports →
 * Genel durum draws (program → sınıf → ünite → kazanım, "n Soru" + %), built by the same builder
 * (buildCategoryActivityAnswerDTOList) so the two views can never disagree on how a node is aggregated.
 *
 * A deneme has no per-question answers, only per-kazanım counts, so each row becomes `questionCount` question
 * scores — 1 for each doğru, 0 for the rest — which is exactly what the builder averages (doğru / soru) and
 * counts (soru). Ids are synthetic and unique per row, so its de-duplication can't merge two questions.
 *
 * Rows the tree can't place — no categoryId, or a category the learner's catalogue doesn't hold — are not
 * dropped: they go under one extra root, `unmatchedTitle`, with the label printed in the file, so the
 * kırılım still sums to the same questions as the istatistikler table.
 */
export const buildMockExamOutcomeTree = (
  scoreRows: MockExamScoreRow[] | null | undefined,
  categoryDTOList: any[] | null | undefined,
  unmatchedTitle: string,
): MockExamOutcomeTreeNode[] => {
  const outcomeRows = (scoreRows || []).filter((row) => row?.scoreScope === "OUTCOME");
  const knownCategoryIdSet = new Set((categoryDTOList || []).map((category: any) => String(category?.id)));
  const placedDetails: any[] = [];
  const unmatched: MockExamOutcomeTreeNode[] = [];
  outcomeRows.forEach((row, rowIndex) => {
    const question = Math.max(0, Math.round(num(row.questionCount)));
    const correct = Math.min(question, Math.max(0, Math.round(num(row.correctCount))));
    if (question === 0) return;
    if (row.categoryId != null && knownCategoryIdSet.has(String(row.categoryId))) {
      for (let index = 0; index < question; index += 1) {
        placedDetails.push({
          activityId: `mock-exam-${rowIndex}-${index}`,
          score: index < correct ? 1 : 0,
          categoryIdList: [row.categoryId],
          empty: false,
        });
      }
      return;
    }
    unmatched.push({
      categoryDTO: { name: row.rawLabel || "—", code: `unmatched-${rowIndex}` },
      subCategoryActivityAnswerDTOList: [],
      aggregatedActivityCount: question,
      aggregatedAveragePercentage: (correct / question) * 100,
    });
  });
  const tree = buildCategoryActivityAnswerDTOList(placedDetails, categoryDTOList as any) as unknown as MockExamOutcomeTreeNode[];
  if (unmatched.length === 0) return tree;
  const unmatchedQuestion = unmatched.reduce((acc, node) => acc + (node.aggregatedActivityCount ?? 0), 0);
  const unmatchedCorrect = unmatched.reduce(
    (acc, node) => acc + ((node.aggregatedAveragePercentage ?? 0) / 100) * (node.aggregatedActivityCount ?? 0),
    0,
  );
  return [
    ...tree,
    {
      categoryDTO: { name: unmatchedTitle, code: "unmatched" },
      subCategoryActivityAnswerDTOList: unmatched,
      aggregatedActivityCount: unmatchedQuestion,
      aggregatedAveragePercentage: unmatchedQuestion > 0 ? (unmatchedCorrect / unmatchedQuestion) * 100 : null,
    },
  ];
};

/** One açık uçlu question of a (kurum içi) deneme, as the karne lists it. */
export interface MockExamOpenEndedLine {
  key: string;
  categoryId: number | null;
  rawLabel: string;
  /** 0–100, or null when the row carried no score. */
  percent: number | null;
}

export interface MockExamOpenEndedSummary {
  questionCount: number;
  /** Mean of the questions' percents (each question weighs its questionCount, normally 1); null when none is scored. */
  averagePercent: number | null;
  lines: MockExamOpenEndedLine[];
}

/**
 * The karne's separate "Açık uçlu" section: the OPEN_ENDED rows only, kept apart from every D/Y/B/net
 * total, lesson total, kazanım net and Tahmini puan — those are built from LESSON / OUTCOME rows alone.
 */
export const summarizeOpenEndedScores = (
  scoreList: MockExamScoreRow[] | null | undefined,
): MockExamOpenEndedSummary => {
  const rows = (scoreList ?? []).filter((row) => row?.scoreScope === OPEN_ENDED_SCORE_SCOPE);
  let questionCount = 0;
  let weightSum = 0;
  let percentSum = 0;
  const lines = rows.map((row, index) => {
    const weight = Math.max(1, num(row.questionCount));
    questionCount += weight;
    const raw = row.percentScore;
    const percent = raw === null || raw === undefined || !Number.isFinite(Number(raw)) ? null : Number(raw);
    if (percent != null) {
      weightSum += weight;
      percentSum += percent * weight;
    }
    return {
      key: `open-${row.categoryId ?? "raw"}-${index}`,
      categoryId: row.categoryId ?? null,
      rawLabel: row.rawLabel ?? "",
      percent,
    };
  });
  return { questionCount, averagePercent: weightSum > 0 ? percentSum / weightSum : null, lines };
};

/** The learner's place among the PUBLISHED şubeler of a deneme — any deneme, imported or kurum içi (report `rankDTO`). */
export interface MockExamRank {
  value?: number | null;
  basis?: "ESTIMATED_SCORE" | "NET" | string | null;
  branchRank?: number | null;
  branchCount?: number | null;
  institutionRank?: number | null;
  institutionCount?: number | null;
  examRank?: number | null;
  examCount?: number | null;
  branchAverage?: number | null;
  institutionAverage?: number | null;
  examAverage?: number | null;
  publishedBranchCount?: number | null;
  totalBranchCount?: number | null;
}

export type MockExamRankScope = "BRANCH" | "INSTITUTION" | "EXAM";

export interface MockExamRankPart {
  scope: MockExamRankScope;
  rank: number;
  count: number | null;
  average: number | null;
}

export interface MockExamRankSummary {
  parts: MockExamRankPart[];
  basis: string | null;
  /** Some şubeler are not published yet, so the rank may still change. */
  partial: boolean;
}

const finiteOrNull = (value: unknown): number | null =>
  value === null || value === undefined || !Number.isFinite(Number(value)) ? null : Number(value);

/** Şube / kurum / genel sıra parts that carry a rank; null when there is no rank to show at all. */
export const mockExamRankSummary = (rank: MockExamRank | null | undefined): MockExamRankSummary | null => {
  if (!rank) return null;
  const scopes: [MockExamRankScope, unknown, unknown, unknown][] = [
    ["BRANCH", rank.branchRank, rank.branchCount, rank.branchAverage],
    ["INSTITUTION", rank.institutionRank, rank.institutionCount, rank.institutionAverage],
    ["EXAM", rank.examRank, rank.examCount, rank.examAverage],
  ];
  const parts: MockExamRankPart[] = [];
  scopes.forEach(([scope, place, count, average]) => {
    const placeValue = finiteOrNull(place);
    if (placeValue == null) return;
    parts.push({ scope, rank: placeValue, count: finiteOrNull(count), average: finiteOrNull(average) });
  });
  if (parts.length === 0) return null;
  const published = finiteOrNull(rank.publishedBranchCount);
  const total = finiteOrNull(rank.totalBranchCount);
  return {
    parts,
    basis: rank.basis ?? null,
    partial: published != null && total != null && published < total,
  };
};
